class ZavyroRampCalculator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-zavyro-ramp-calculator]');
    this.result = this.querySelector('[data-zavyro-ramp-result]');
    if (!this.form || !this.result) return;
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.calculate();
    });
  }

  calculate() {
    const data = new FormData(this.form);
    const rise = Number(data.get('rise'));
    const run = Number(data.get('run'));
    const unit = data.get('unit') === 'cm' ? 'cm' : 'in';
    const context = String(data.get('context') || 'unknown');
    if (!Number.isFinite(rise) || !Number.isFinite(run) || rise <= 0 || run <= 0) {
      this.result.hidden = false;
      this.result.innerHTML = '<strong>Check both measurements.</strong><p>Enter a vertical height and available floor space greater than zero.</p>';
      return;
    }
    const length = Math.hypot(rise, run);
    const angle = Math.atan2(rise, run) * (180 / Math.PI);
    const contextNote = context === 'senior'
      ? 'Because you selected an older or less-steady dog, treat this as a planning result and discuss new or worsening mobility changes with your veterinarian.'
      : context === 'confident'
        ? 'Even for a confident dog, verify traction, width, weight rating and stable placement before use.'
        : 'Observe your dog on ordinary steps and ask your veterinarian about unexplained hesitation, pain or balance changes.';
    this.result.hidden = false;
    this.result.innerHTML = `
      <p class="zavyro-editorial__eyebrow">Your measured setup</p>
      <h3>About ${length.toFixed(1)} ${unit} of ramp surface</h3>
      <dl>
        <div><dt>Calculated angle</dt><dd>${angle.toFixed(1)}&deg;</dd></div>
        <div><dt>Vertical rise</dt><dd>${rise.toFixed(1)} ${unit}</dd></div>
        <div><dt>Horizontal floor space</dt><dd>${run.toFixed(1)} ${unit}</dd></div>
      </dl>
      <p>${contextNote}</p>
      <p><strong>Important:</strong> This is geometry, not a veterinary recommendation or product certification. A shorter product would produce a steeper setup.</p>
      <p><a class="button button--secondary" href="/collections/safer-movement" data-zavyro-link-type="collection" data-zavyro-source="calculator-result">Compare Safer Movement options using this result</a></p>`;
  }
}

if (!customElements.get('zavyro-ramp-calculator')) {
  customElements.define('zavyro-ramp-calculator', ZavyroRampCalculator);
}

class ZavyroBedCalculator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-zavyro-bed-form]');
    this.result = this.querySelector('[data-zavyro-bed-result]');
    if (!this.form || !this.result) return;
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.calculate();
    });
  }

  calculate() {
    const data = new FormData(this.form);
    const length = Number(data.get('length'));
    const width = Number(data.get('width'));
    const unit = data.get('unit') === 'cm' ? 'cm' : 'in';
    const position = ['stretched', 'curled', 'mixed'].includes(data.get('position')) ? data.get('position') : 'mixed';
    if (!Number.isFinite(length) || !Number.isFinite(width) || length <= 0 || width <= 0) {
      this.result.hidden = false;
      this.result.innerHTML = '<strong>Check both measurements.</strong><p>Enter a resting length and width greater than zero.</p>';
      return;
    }
    const factor = unit === 'cm' ? 2.54 : 1;
    const allowance = {
      stretched: { length: 12 * factor, width: 8 * factor, label: 'mostly stretched' },
      curled: { length: 8 * factor, width: 8 * factor, label: 'mostly curled' },
      mixed: { length: 12 * factor, width: 12 * factor, label: 'mixed positions' },
    }[position];
    const minimumLength = length + allowance.length;
    const minimumWidth = width + allowance.width;
    this.result.hidden = false;
    this.result.innerHTML = `
      <p class="zavyro-editorial__eyebrow">Minimum usable surface</p>
      <h3>About ${minimumLength.toFixed(1)} &times; ${minimumWidth.toFixed(1)} ${unit}</h3>
      <p>Based on ${length.toFixed(1)} &times; ${width.toFixed(1)} ${unit} and a transparent allowance for ${allowance.label} sleep.</p>
      <p><strong>Compare usable interior dimensions.</strong> Bolsters and raised edges can make the outside dimensions larger than the actual sleeping surface.</p>
      <p>This planning result does not evaluate materials, support, entry height, traction or individual comfort.</p>
      <p><a class="button button--secondary" href="/collections/better-rest" data-zavyro-link-type="collection" data-zavyro-source="calculator-result">Compare Better Rest options by usable surface</a></p>`;
  }
}

if (!customElements.get('zavyro-bed-calculator')) {
  customElements.define('zavyro-bed-calculator', ZavyroBedCalculator);
}

class ZavyroBowlCalculator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-zavyro-bowl-form]');
    this.result = this.querySelector('[data-zavyro-bowl-result]');
    if (!this.form || !this.result) return;
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.calculate();
    });
  }

  calculate() {
    const data = new FormData(this.form);
    const shoulder = Number(data.get('shoulder'));
    const depth = Number(data.get('depth'));
    const unit = data.get('unit') === 'cm' ? 'cm' : 'in';
    const species = data.get('species') === 'cat' ? 'cat' : 'dog';
    if (!Number.isFinite(shoulder) || !Number.isFinite(depth) || shoulder <= 0 || depth < 0 || depth >= shoulder) {
      this.result.hidden = false;
      this.result.innerHTML = '<strong>Check the measurements.</strong><p>Shoulder height must be greater than zero, and bowl depth must be smaller than shoulder height.</p>';
      return;
    }
    const ratios = species === 'cat' ? [0.35, 0.5] : [0.55, 0.7];
    const rimLow = shoulder * ratios[0];
    const rimHigh = shoulder * ratios[1];
    const standLow = Math.max(0, rimLow - depth);
    const standHigh = Math.max(0, rimHigh - depth);
    this.result.hidden = false;
    this.result.innerHTML = `
      <p class="zavyro-editorial__eyebrow">Transparent trial range</p>
      <h3>Rim: ${rimLow.toFixed(1)}&ndash;${rimHigh.toFixed(1)} ${unit}</h3>
      <dl>
        <div><dt>Estimated stand</dt><dd>${standLow.toFixed(1)}&ndash;${standHigh.toFixed(1)} ${unit}</dd></div>
        <div><dt>Measured shoulder</dt><dd>${shoulder.toFixed(1)} ${unit}</dd></div>
        <div><dt>Measured bowl depth</dt><dd>${depth.toFixed(1)} ${unit}</dd></div>
      </dl>
      <p>ZAVYRO's planning method uses ${Math.round(ratios[0] * 100)}&ndash;${Math.round(ratios[1] * 100)}% of measured shoulder height for a ${species}, then subtracts bowl depth to estimate stand height.</p>
      <p><strong>Observe one normal meal.</strong> Verify stability and stop the trial if eating becomes harder. This is not a veterinary prescription or a health threshold.</p>
      <p><a class="button button--secondary" href="/collections/easier-mealtimes" data-zavyro-link-type="collection" data-zavyro-source="calculator-result">Compare Easier Mealtimes options using this range</a></p>`;
  }
}

if (!customElements.get('zavyro-bowl-calculator')) {
  customElements.define('zavyro-bowl-calculator', ZavyroBowlCalculator);
}

class ZavyroHomeChecklist extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-zavyro-checklist-form]');
    this.count = this.querySelector('[data-zavyro-checklist-count]');
    this.progress = this.querySelector('[data-zavyro-checklist-progress]');
    this.printButton = this.querySelector('[data-zavyro-checklist-print]');
    if (!this.form || !this.count || !this.progress) return;
    this.inputs = Array.from(this.form.querySelectorAll('input[type="checkbox"]'));
    this.form.addEventListener('change', () => this.update());
    this.form.addEventListener('reset', () => setTimeout(() => this.update(), 0));
    if (this.printButton) this.printButton.addEventListener('click', () => window.print());
    this.update();
  }

  update() {
    const completed = this.inputs.filter((input) => input.checked).length;
    const total = this.inputs.length;
    this.count.textContent = `${completed} of ${total} checks completed`;
    this.progress.max = total;
    this.progress.value = completed;
    this.progress.textContent = total ? `${Math.round((completed / total) * 100)}%` : '0%';
  }
}

if (!customElements.get('zavyro-home-checklist')) {
  customElements.define('zavyro-home-checklist', ZavyroHomeChecklist);
}
